import { find, isEmpty, isUndefined } from 'es-toolkit/compat';
import clsx from 'clsx';

import { IconClock } from '@tock/icons';
import { LegacyPlaceholderBusinessImage } from '@tock/images';
import { Typography } from 'omakase';
import { consumer } from '@tock/coretypes';
import { getModifierClassName } from 'utils/widgetUtils';
import Anchor from 'components/Anchor';
import LazyImage from '@tock/shared/components/LazyImage';
import NewlineEscapedText from '@tock/shared/components/NewlineEscapedText';
import PureBaseComponent from '@tock/shared/components/PureBaseComponent';

interface Props {
  title: JSX.Element | string;
  subtitle?: JSX.Element | string;
  subtitleString: string;
  href?: string;
  body?: string;
  highlightedNote?: JSX.Element | string;
  widgetModifiers?: consumer.WidgetModifier[];
  imgSrc?: string;
  altText: string;
  dominantColor: string;
  handleLinkClick?(): void;
  linkText?: string;
  showBodyByDefault: boolean;
  variant?: string;
}

export default class PhotoItemDisplay extends PureBaseComponent<Props> {
  bodyClassName: string;
  highlightedNoteClassName: string;

  constructor(props: Props) {
    super(props);
    const showDescription = find(props.widgetModifiers, { key: 'showDescription' });
    const showReleasesInfo = find(props.widgetModifiers, { key: 'showReleasesInfo' });
    this.bodyClassName = showDescription
      ? getModifierClassName(
          clsx('PhotoItemDisplay-body', 'is-visible'),
          showDescription.breakpoint
        )
      : clsx('PhotoItemDisplay-body', { 'is-visible': props.showBodyByDefault });
    this.highlightedNoteClassName = clsx('PhotoItemDisplay-highlightedNote', {
      'is-visible': !!showReleasesInfo,
    });
  }

  render() {
    const {
      body,
      handleLinkClick,
      href,
      imgSrc,
      dominantColor,
      altText,
      highlightedNote,
      linkText,
      variant,
      subtitle,
      subtitleString,
      title,
    } = this.props;
    const variantClassName = variant ? `PhotoItemDisplay--${variant}` : '';
    const className = clsx('PhotoItemDisplay', variantClassName);
    return (
      <div className={className}>
        <Anchor
          className="PhotoItemDisplay-link"
          title={title + ', ' + subtitleString}
          href={href}
          onClick={handleLinkClick}
        >
          <div
            className="PhotoItemDisplay-imageContainer"
            style={{ backgroundColor: dominantColor }}
          >
            <LazyImage
              preload={false}
              backgroundColor={dominantColor}
              alt={altText || title + ', ' + subtitleString}
              className="PhotoItemDisplay-image"
              src={(imgSrc && !isEmpty(imgSrc) && imgSrc) || LegacyPlaceholderBusinessImage}
            />
          </div>
          {!isUndefined(title) && (
            <h3 data-testid="result-title" className="PhotoItemDisplay-title" aria-hidden="true">
              {typeof title === 'string' && (
                <Typography color="inherit" component="span" variant="heading03">
                  {title}
                </Typography>
              )}
              {typeof title !== 'string' && title}
            </h3>
          )}
          {!isUndefined(subtitle) && (
            <div className="PhotoItemDisplay-subtitle" aria-hidden="true">
              {subtitle}
            </div>
          )}
          {!isEmpty(highlightedNote) && (
            <div className={this.highlightedNoteClassName}>
              <IconClock className="PhotoItemDisplay-highlightedNoteIcon" />
              <Typography component="span" variant="body02Strong">
                <span className="PhotoItemDisplay-highlightedNoteContent">{highlightedNote}</span>
              </Typography>
            </div>
          )}
          {!isUndefined(body) && (
            <div className={this.bodyClassName}>
              <Typography component="span" variant="caption">
                <NewlineEscapedText text={body} />
              </Typography>
            </div>
          )}
          {!isUndefined(linkText) && (
            <div className="PhotoItemDisplay-textlink">
              <Typography component="span" variant="body02Strong">
                <NewlineEscapedText text={linkText} />
              </Typography>
            </div>
          )}
        </Anchor>
      </div>
    );
  }
}
